/**
 * Face group surface visualization — tints faces by face-group membership
 * while in edit mode. Optional, gated by `editMode.showFaceGroupColors`.
 *
 * Membership semantics:
 *  - A face belongs to a group iff its index is in the group's `faces` set.
 *    No vertex inference — face groups are explicit per-face memberships
 *    (CLAUDE.md "Never infer face selection from vertex membership").
 *  - Where multiple groups claim the same face, the later group in
 *    `faceAttributes.groups` wins (overlay precedence).
 *  - Faces in no group stay untinted; the base material shows through.
 *
 * Rendering follows `VertexGroupColorOverlay`: one `<mesh>` per non-empty
 * group bucket, all sharing a single live position attribute.
 */

import { useEffect, useMemo } from "react";
import * as THREE from "three/webgpu";
import { useResolvedLiveEditGeometry } from "../app/useLiveEditPreview.ts";
import type { FaceGroup, PolygonTopology } from "../modeling/types.ts";
import { updateSharedVertexPositions } from "./EditModeRenderer.tsx";
import { createOverlayFaceMaterial } from "./editDots.ts";
import { groupColorAt } from "./groupColors.ts";
import { useDisposeMany } from "./hooks/useDispose.ts";

interface FaceBucket {
  groupIdx: number;
  indices: Uint32Array;
}

/**
 * Build a per-face → group-index array for face groups, applying overlay
 * precedence (later groups win). Returns `null` when no group has members.
 */
function resolveFaceGroupIndices(
  topology: PolygonTopology,
  groups: ReadonlyArray<FaceGroup>,
): Int32Array | null {
  const faceCount =
    topology.offsets.length === 0 ? 0 : topology.offsets.length - 1;
  if (faceCount === 0 || groups.length === 0) return null;
  let any = false;
  const out = new Int32Array(faceCount);
  out.fill(-1);
  for (let gi = 0; gi < groups.length; gi++) {
    for (const f of groups[gi].faces) {
      if (f >= 0 && f < faceCount) {
        out[f] = gi;
        any = true;
      }
    }
  }
  return any ? out : null;
}

function bucketFacesByGroup(
  topology: PolygonTopology,
  faceGroupIndices: Int32Array,
  hiddenFaces: Set<number>,
): FaceBucket[] {
  const { offsets, corners } = topology;
  let groupMax = -1;
  for (let i = 0; i < faceGroupIndices.length; i++) {
    if (faceGroupIndices[i] > groupMax) groupMax = faceGroupIndices[i];
  }
  if (groupMax < 0) return [];

  const triCounts = new Uint32Array(groupMax + 1);
  for (let f = 0; f < faceGroupIndices.length; f++) {
    const gi = faceGroupIndices[f];
    if (gi < 0) continue;
    if (hiddenFaces.has(f)) continue;
    const size = offsets[f + 1] - offsets[f];
    if (size >= 3) triCounts[gi] += size - 2;
  }

  const buckets: FaceBucket[] = [];
  const writers: Uint32Array[] = [];
  const writePositions = new Uint32Array(groupMax + 1);
  for (let gi = 0; gi <= groupMax; gi++) {
    if (triCounts[gi] === 0) continue;
    const buf = new Uint32Array(triCounts[gi] * 3);
    writers[gi] = buf;
    buckets.push({ groupIdx: gi, indices: buf });
  }

  for (let f = 0; f < faceGroupIndices.length; f++) {
    const gi = faceGroupIndices[f];
    if (gi < 0) continue;
    if (hiddenFaces.has(f)) continue;
    const start = offsets[f];
    const end = offsets[f + 1];
    const size = end - start;
    if (size < 3) continue;
    const buf = writers[gi];
    if (!buf) continue;
    let w = writePositions[gi];
    const v0 = corners[start];
    for (let i = 1; i < size - 1; i++) {
      buf[w++] = v0;
      buf[w++] = corners[start + i];
      buf[w++] = corners[start + i + 1];
    }
    writePositions[gi] = w;
  }
  return buckets;
}

interface FaceGroupColorOverlayProps {
  objectId: string;
  vertices: Float32Array;
  topology: PolygonTopology;
  groups: ReadonlyArray<FaceGroup>;
  hiddenFaces: Set<number>;
  dirtyVertices: Uint32Array | null;
  xray: boolean;
}

export function FaceGroupColorOverlay({
  objectId,
  vertices,
  topology,
  groups,
  hiddenFaces,
  dirtyVertices,
  xray,
}: FaceGroupColorOverlayProps) {
  const preview = useResolvedLiveEditGeometry(
    objectId,
    vertices,
    dirtyVertices,
  );
  const previewVertices = preview.vertices;
  const previewDirtyVertices = preview.dirtyVertices;
  const previewDirtyPositions = preview.dirtyVertexPositions;

  const faceGroupIndices = useMemo(
    () => resolveFaceGroupIndices(topology, groups),
    [topology, groups],
  );

  const buckets = useMemo(() => {
    if (!faceGroupIndices) return [];
    return bucketFacesByGroup(topology, faceGroupIndices, hiddenFaces);
  }, [topology, faceGroupIndices, hiddenFaces]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: depend on length only — array reference changes every drag frame, but the GPU buffer must persist
  const positionAttr = useMemo(
    () => new THREE.BufferAttribute(new Float32Array(previewVertices), 3),
    [previewVertices.length],
  );

  const bucketGeos = useMemo(() => {
    const geos: THREE.BufferGeometry[] = [];
    for (const b of buckets) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", positionAttr);
      geo.setIndex(new THREE.BufferAttribute(b.indices, 1));
      geos.push(geo);
    }
    return geos;
  }, [buckets, positionAttr]);
  useDisposeMany(...bucketGeos);

  // Offset face-group palette away from vertex-group palette so the two
  // overlays remain visually distinct when both are toggled on.
  const bucketMats = useMemo(
    () =>
      buckets.map((b) =>
        createOverlayFaceMaterial({
          color: `#${groupColorAt(b.groupIdx + 16).getHexString()}`,
          opacity: 0.55,
          depthTest: !xray,
          side: xray ? THREE.DoubleSide : THREE.FrontSide,
        }),
      ),
    [buckets, xray],
  );
  useDisposeMany(...bucketMats);

  useEffect(() => {
    if (positionAttr.array.length === previewVertices.length) {
      updateSharedVertexPositions(
        positionAttr,
        previewVertices,
        previewDirtyVertices,
        previewDirtyPositions,
      );
    }
  }, [
    positionAttr,
    previewVertices,
    previewDirtyVertices,
    previewDirtyPositions,
  ]);

  if (bucketGeos.length === 0) return null;

  return (
    <>
      {bucketGeos.map((geo, i) => (
        <mesh
          // biome-ignore lint/suspicious/noArrayIndexKey: buckets are stable per memo
          key={i}
          geometry={geo}
          material={bucketMats[i]}
          renderOrder={0}
        />
      ))}
    </>
  );
}
